import { useState, useEffect, useCallback } from "react";
import {
  Shield, Activity, RefreshCw, Download, Wrench, ToggleLeft, ToggleRight,
  CheckCircle2, XCircle, FileText, AlertTriangle, Lock, Zap, ShieldAlert
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const ROOT_EMAIL = "jp-empire@eay.jp";
const SUPABASE_PROJECT_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID || "bbygnqkxijbplzuhktfm"}.supabase.co`;

interface TestResult {
  name: string;
  status: string;
  detail: string;
  durationMs: number;
}

interface SelftestReport {
  timestamp: string;
  summary: { total: number; passed: number; failed: number };
  tests: TestResult[];
}

interface FeatureFlag {
  id: string;
  name: string;
  enabled: boolean;
  description: string | null;
}

interface AuditEntry {
  id: string;
  action: string;
  target_type: string | null;
  created_at: string;
  user_id: string | null;
  ip_address: string | null;
}

const RootOwner = () => {
  const { user, session, roles } = useAuth();
  const [tab, setTab] = useState<"status" | "selftest" | "autofix" | "flags" | "audit">("status");

  // Health
  const [health, setHealth] = useState<any>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Selftest
  const [report, setReport] = useState<SelftestReport | null>(null);
  const [selftestLoading, setSelftestLoading] = useState(false);

  // Autofix
  const [autofixLoading, setAutofixLoading] = useState<string | null>(null);
  const [autofixResult, setAutofixResult] = useState<any>(null);

  // Flags
  const [flags, setFlags] = useState<FeatureFlag[]>([]);

  // Audit
  const [auditLogs, setAuditLogs] = useState<AuditEntry[]>([]);

  // Server-side guard
  const [verified, setVerified] = useState<boolean | null>(null);

  useEffect(() => {
    (async () => {
      if (!user || user.email?.toLowerCase() !== ROOT_EMAIL.toLowerCase()) {
        setVerified(false);
        return;
      }
      try {
        const { data } = await supabase.rpc("is_admin", { _user_id: user.id });
        setVerified(data === true);
      } catch {
        setVerified(false);
      }
    })();
  }, [user]);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/health`);
      setHealth(await res.json());
    } catch (e: any) {
      toast({ title: "Health check failed", description: e.message, variant: "destructive" });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const runSelftest = useCallback(async () => {
    if (!session?.access_token) return;
    setSelftestLoading(true);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-selftest`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.ok) {
        setReport(data.report);
        toast({ title: `Selftest complete: ${data.report.summary.passed}/${data.report.summary.total} passed` });
      } else {
        toast({ title: "Selftest failed", description: data.error, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSelftestLoading(false);
    }
  }, [session]);

  const fetchLatestReport = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-selftest`, {
        method: "GET",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (data.ok && data.report?.report_json) {
        setReport(data.report.report_json as SelftestReport);
      }
    } catch {}
  }, [session]);

  const runAutofix = useCallback(async (action: string) => {
    if (!session?.access_token) return;
    setAutofixLoading(action);
    setAutofixResult(null);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-autofix`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      setAutofixResult(data);
      toast({ title: data.ok ? "✅ Autofix complete" : "Autofix failed", description: data.detail || data.error });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setAutofixLoading(null);
    }
  }, [session]);

  const fetchFlags = useCallback(async () => {
    const { data } = await supabase.from("feature_flags").select("*").order("name");
    if (data) setFlags(data as FeatureFlag[]);
  }, []);

  const toggleFlag = async (flag: FeatureFlag) => {
    await supabase.from("feature_flags").update({ enabled: !flag.enabled }).eq("id", flag.id);
    setFlags(prev => prev.map(f => f.id === flag.id ? { ...f, enabled: !f.enabled } : f));
    toast({ title: `${flag.name} ${!flag.enabled ? "enabled" : "disabled"}` });
  };

  const fetchAudit = useCallback(async () => {
    const { data } = await supabase.from("audit_logs").select("id, action, target_type, created_at, user_id, ip_address").order("created_at", { ascending: false }).limit(100);
    if (data) setAuditLogs(data as AuditEntry[]);
  }, []);

  useEffect(() => {
    if (verified !== true) return;
    if (tab === "status") fetchHealth();
    if (tab === "selftest") fetchLatestReport();
    if (tab === "flags") fetchFlags();
    if (tab === "audit") fetchAudit();
  }, [tab, verified]);

  const exportReport = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `selftest-report-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (verified === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-dark">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (verified === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-dark">
        <div className="text-center">
          <ShieldAlert className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Root Owner access only.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "status", label: "System Status", icon: Activity },
    { id: "selftest", label: "Selftest", icon: Shield },
    { id: "autofix", label: "Autofix", icon: Wrench },
    { id: "flags", label: "Feature Flags", icon: Zap },
    { id: "audit", label: "Audit Logs", icon: FileText },
  ] as const;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-dark">
      <div className="fixed inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
      <Header />
      <div className="flex-1 relative z-10 max-w-6xl mx-auto w-full p-6">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Root Owner Panel</h1>
          <span className="text-xs bg-destructive/20 text-destructive px-2 py-0.5 rounded-full font-medium">SUPER_ADMIN</span>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-primary text-primary-foreground" : "bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}>
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Status */}
        {tab === "status" && (
          <div className="glass-card p-6 border border-border/50 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Health Check</h3>
              <button onClick={fetchHealth} disabled={healthLoading}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 text-primary rounded-lg text-sm hover:bg-primary/30 disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${healthLoading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
            {health && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><span className="text-muted-foreground">Status:</span> <span className={health.ok ? "text-primary" : "text-destructive"}>{health.ok ? "OK" : "ERROR"}</span></div>
                <div><span className="text-muted-foreground">Version:</span> <span className="text-foreground">{health.version}</span></div>
                <div><span className="text-muted-foreground">Platform:</span> <span className="text-foreground">{health.platform}</span></div>
                <div><span className="text-muted-foreground">Time:</span> <span className="text-foreground font-mono text-xs">{health.timestamp}</span></div>
              </div>
            )}
          </div>
        )}

        {/* Selftest */}
        {tab === "selftest" && (
          <div className="space-y-4">
            <div className="flex gap-3">
              <button onClick={runSelftest} disabled={selftestLoading}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
                <Shield className={`w-4 h-4 ${selftestLoading ? "animate-spin" : ""}`} /> Run Full Selftest
              </button>
              <button onClick={fetchLatestReport} className="flex items-center gap-2 px-4 py-2 bg-muted/30 text-muted-foreground rounded-lg text-sm hover:bg-muted/50">
                <RefreshCw className="w-4 h-4" /> Load Latest
              </button>
              {report && (
                <button onClick={exportReport} className="flex items-center gap-2 px-4 py-2 bg-muted/30 text-muted-foreground rounded-lg text-sm hover:bg-muted/50">
                  <Download className="w-4 h-4" /> Export JSON
                </button>
              )}
            </div>
            {report && (
              <div className="glass-card border border-border/50 overflow-hidden">
                <div className="p-4 border-b border-border/30 flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {report.summary.passed}/{report.summary.total} passed
                  </span>
                  {report.summary.failed > 0 && (
                    <span className="text-xs bg-destructive/20 text-destructive px-2 py-0.5 rounded-full">
                      {report.summary.failed} failed
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground ml-auto font-mono">{report.timestamp}</span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/30 bg-muted/10">
                      <th className="text-left p-3 text-muted-foreground text-xs font-medium">Test</th>
                      <th className="text-left p-3 text-muted-foreground text-xs font-medium">Status</th>
                      <th className="text-left p-3 text-muted-foreground text-xs font-medium">Detail</th>
                      <th className="text-right p-3 text-muted-foreground text-xs font-medium">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.tests.map((t, i) => (
                      <tr key={i} className="border-b border-border/20">
                        <td className="p-3 font-mono text-xs text-foreground">{t.name}</td>
                        <td className="p-3">
                          {t.status === "PASS" ? (
                            <span className="inline-flex items-center gap-1 text-primary text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> PASS</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-destructive text-xs"><XCircle className="w-3.5 h-3.5" /> FAIL</span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground max-w-xs truncate">{t.detail}</td>
                        <td className="p-3 text-right text-xs text-muted-foreground">{t.durationMs}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Autofix */}
        {tab === "autofix" && (
          <div className="space-y-4">
            <div className="glass-card p-5 border border-border/50 space-y-3">
              <h3 className="font-semibold text-foreground flex items-center gap-2"><Wrench className="w-4 h-4 text-primary" /> Safe Auto-Repair Actions</h3>
              <p className="text-xs text-muted-foreground">Each action is audit-logged. Only safe, idempotent repairs.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { action: "reregister_webhook", label: "Re-register Telegram Webhook", icon: RefreshCw },
                  { action: "repair_root_owner", label: "Repair Root Owner Role", icon: Shield },
                  { action: "check_ai", label: "Check AI Provider", icon: Zap },
                ].map(a => (
                  <button key={a.action} onClick={() => runAutofix(a.action)} disabled={autofixLoading === a.action}
                    className="flex items-center gap-2 p-3 bg-muted/20 border border-border/30 rounded-lg text-sm text-foreground hover:bg-muted/30 disabled:opacity-50 transition-colors">
                    <a.icon className={`w-4 h-4 text-primary ${autofixLoading === a.action ? "animate-spin" : ""}`} />
                    {a.label}
                  </button>
                ))}
              </div>
              {autofixResult && (
                <div className={`mt-3 p-3 rounded-lg text-xs font-mono ${autofixResult.ok ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}>
                  {JSON.stringify(autofixResult, null, 2)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Feature Flags */}
        {tab === "flags" && (
          <div className="glass-card border border-border/50 overflow-hidden">
            <div className="p-4 border-b border-border/30">
              <h3 className="font-semibold text-foreground">Feature Flags</h3>
            </div>
            <div className="divide-y divide-border/20">
              {flags.map(flag => (
                <div key={flag.id} className="flex items-center justify-between p-4 hover:bg-muted/10">
                  <div>
                    <div className="text-sm font-medium text-foreground">{flag.name}</div>
                    {flag.description && <div className="text-xs text-muted-foreground">{flag.description}</div>}
                  </div>
                  <button onClick={() => toggleFlag(flag)} className="p-1">
                    {flag.enabled ? <ToggleRight className="w-6 h-6 text-primary" /> : <ToggleLeft className="w-6 h-6 text-muted-foreground" />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit Logs */}
        {tab === "audit" && (
          <div className="glass-card border border-border/50 overflow-hidden">
            <div className="p-4 border-b border-border/30 flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Recent Audit Logs</h3>
              <button onClick={fetchAudit} className="text-xs text-primary hover:underline">Refresh</button>
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/20">
                  <tr className="border-b border-border/30">
                    <th className="text-left p-3 text-muted-foreground text-xs font-medium">Action</th>
                    <th className="text-left p-3 text-muted-foreground text-xs font-medium">Target</th>
                    <th className="text-left p-3 text-muted-foreground text-xs font-medium">IP</th>
                    <th className="text-left p-3 text-muted-foreground text-xs font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id} className="border-b border-border/20">
                      <td className="p-3 text-xs font-mono text-foreground">{log.action}</td>
                      <td className="p-3 text-xs text-muted-foreground">{log.target_type || "—"}</td>
                      <td className="p-3 text-xs text-muted-foreground font-mono">{log.ip_address || "—"}</td>
                      <td className="p-3 text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default RootOwner;
